import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllDaysBetweenDates, getDateMinusAndPlusXDays } from '@he-novation/utils/datetime';
import { useAtom } from 'jotai';
import {
    calendarActiveCenteredDateAtom,
    calendarActiveFiltersAtom,
    calendarActiveGrouperFilters,
    calendarDateRangeAtom,
    calendarGlobalRowsAtom,
    calendarGrouperAtom,
    calendarItemColorTypeAtom,
    calendarItemsAtom,
    calendarProjectAtom,
    calendarZoomAtom
} from '../atoms/calendar.atoms';

import { FilterValue } from '$components/Filters/FilterCheckboxes';
import {
    DEFAULT_DAYS_INTERVAL,
    mapCalendarItemsToRows
} from '$components/ScrollCalendar/ScrollCalendar.helpers';
import {
    CalendarFilterEnum,
    CalendarItemColorType,
    ScrollCalendarGrouper,
    ScrollCalendarItemType
} from '$components/ScrollCalendar/ScrollCalendar.types';
import { clientLabelsSelector } from '$redux/client/clientSelectors';
import { appendEvents, fetchEvents } from '$redux/content/events/eventsActions';
import { eventsSelector } from '$redux/content/events/eventsSelectors';
import { currentUserUuidSelector } from '$redux/user/userSelectors';
import { ClientLabelType } from '$types/clientTypes';

export const useCalendar = () => {
    const [searchTerm, setSearchTerm] = useState('');

    const [grouperFilters, setGrouperFilters] = useAtom(calendarActiveGrouperFilters);
    const [zoom, setZoom] = useAtom(calendarZoomAtom);
    const [currentGrouper, setCurrentGrouper] = useAtom(calendarGrouperAtom);
    const [activeFilters, setActiveFilters] = useAtom(calendarActiveFiltersAtom);
    const [items, setItems] = useAtom(calendarItemsAtom);
    const [itemColorType, setItemColorType] = useAtom(calendarItemColorTypeAtom);
    const [dateRange, setDateRange] = useAtom(calendarDateRangeAtom);
    const [project, setProject] = useAtom(calendarProjectAtom);
    const [globalRows, setGlobalRows] = useAtom(calendarGlobalRowsAtom);
    const [activeCenteredDate, setActiveCenteredDate] = useAtom(calendarActiveCenteredDateAtom);

    const { currentUserUuid } = useSelector(currentUserUuidSelector);
    const { isLoading, isUpdating, dateRangeUpdating } = useSelector(eventsSelector);
    const contactLabels = useSelector(clientLabelsSelector(ClientLabelType.Contact));
    const itemLabels = useSelector(clientLabelsSelector(ClientLabelType.Item));

    const dispatch = useDispatch();

    const updateCalendarEvents = useCallback(
        async (start: Date, end: Date) => {
            try {
                await dispatch(appendEvents({ projectUuid: project?.uuid, start, end }));
            } catch (error) {
                console.error('Failed to update calendar events', error);
            }
        },
        [dispatch, project?.uuid]
    );

    const resetState = () => {
        setSearchTerm('');
        setGrouperFilters([]);
        setActiveFilters({
            [CalendarFilterEnum.Teams]: [],
            [CalendarFilterEnum.Labels]: [],
            [CalendarFilterEnum.Authors]: []
        });
        setItemColorType(CalendarItemColorType.Label);
        setProject(undefined);
        setGlobalRows(undefined);
        setItems([]);
        setActiveCenteredDate(new Date());
    };

    const fetchCalendarEvents = async (start: Date, end: Date) => {
        await dispatch(fetchEvents({ start, end, projectUuid: project?.uuid }));
    };

    const filterByLabels = (items: ScrollCalendarItemType[]): ScrollCalendarItemType[] => {
        if (activeFilters.Labels.length) {
            const filtered = items.filter((item) =>
                item.labels?.some((label) => activeFilters.Labels.includes(label.uid))
            );
            if (activeFilters.Labels.includes('none')) {
                return [...filtered, ...items.filter((item) => !item.labels?.length)];
            }
            return filtered;
        }
        return items;
    };

    const filterByTeams = (items: ScrollCalendarItemType[]): ScrollCalendarItemType[] => {
        if (activeFilters.Teams.length) {
            const filtered = items.filter((item) =>
                item.teams?.some((team) => activeFilters.Teams.includes(team.uuid))
            );
            if (activeFilters.Teams.includes('none')) {
                return [...filtered, ...items.filter((item) => !item.teams?.length)];
            }
            return filtered;
        }
        return items;
    };

    const filterByAuthors = (items: ScrollCalendarItemType[]): ScrollCalendarItemType[] => {
        if (activeFilters.Authors.length) {
            const filtered = items.filter((item) =>
                activeFilters.Authors.includes(item.owner?.uuid)
            );
            if (activeFilters.Authors.includes('none')) {
                return [...filtered, ...items.filter((item) => !item.owner)];
            }
            return filtered;
        }
        return items;
    };

    const filteredItems: ScrollCalendarItemType[] = useMemo(() => {
        return filterByAuthors(filterByTeams(filterByLabels(items)));
    }, [items, activeFilters]);

    const rows = useMemo(() => {
        return mapCalendarItemsToRows({
            items: filteredItems || [],
            grouper: currentGrouper,
            searchTerm,
            globalRows,
            grouperFilter: grouperFilters,
            currentUserUuid
        });
    }, [filteredItems, currentGrouper, searchTerm, globalRows, grouperFilters, currentUserUuid]);

    const grouperFilterValues: FilterValue[] = useMemo(() => {
        switch (currentGrouper) {
            case ScrollCalendarGrouper.Users: {
                return (
                    contactLabels?.map(
                        (label) =>
                            ({
                                label: label?.name,
                                id: label?.uid
                            } as FilterValue)
                    ) || []
                );
            }
            case ScrollCalendarGrouper.Items: {
                return (
                    itemLabels?.map(
                        (label) =>
                            ({
                                label: label?.name,
                                id: label?.uid
                            } as FilterValue)
                    ) || []
                );
            }
        }
        return [];
    }, [filteredItems, currentGrouper]);

    const daysInterval = useMemo(
        () => getAllDaysBetweenDates(dateRange[0], dateRange[1]).map((date) => date.toISOString()),
        [dateRange]
    );

    const goToDate = async (date: Date) => {
        const scrolledToDate = scrollToDate(date);
        if (!scrolledToDate) {
            const { minusXDays, plusXDays } = getDateMinusAndPlusXDays(date, DEFAULT_DAYS_INTERVAL);
            await fetchCalendarEvents(minusXDays, plusXDays);
            setDateRange([minusXDays, plusXDays]);
            scrollToDate(date);
            setActiveCenteredDate(date);
        }
    };

    const scrollToDate = (date: Date, options?: ScrollIntoViewOptions): boolean => {
        const dateElement = document.querySelector(
            `[data-day="${date.toLocaleDateString('fr-FR')}"]`
        );
        if (dateElement) {
            dateElement.scrollIntoView(
                options ?? { behavior: 'smooth', block: 'center', inline: 'center' }
            );
            return true;
        }
        return false;
    };

    return {
        items,
        project,
        itemColorType,
        zoom,
        dateRange,
        activeCenteredDate,
        activeFilters,
        currentGrouper,
        goToDate,
        scrollToDate,
        isLoading,
        rows,
        daysInterval,
        isUpdating,
        dateRangeUpdating,
        isInProject: !!project,
        setZoom,
        setSearchTerm,
        setActiveFilters,
        setItemColorType,
        setProject,
        setGlobalRows,
        setActiveCenteredDate,
        setItems,
        setDateRange,
        setCurrentGrouper,
        updateCalendarEvents,
        grouperFilterValues,
        setGrouperFilters,
        grouperFilters,
        resetState
    };
};
